import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import AppContext from '../../../context/appContext'
import { ELIGIBILITY_QUERY, useEligibilityCheck } from '../../../hooks/data'
import { EligibilityArea } from '../../../types/apiModels'
import { useEligibilityStatus } from './useEligibilityStatus'

interface EligibilityCheckContextType {
  eligibilityCheckAreas: EligibilityArea[]
  isLoadingEligibilityChecks: boolean
  setSelectedEligibilityAreaIds: (ids: Iterable<string>) => void
  createEligibilityCheck: () => Promise<void>
  isEligibilityVisible: boolean
  setEligibilityVisible: Dispatch<SetStateAction<boolean>>
  isRunningEligibility: boolean
  startEligibilityCheck: () => void
  clearEligibilityCheck: () => void
}

const EligibilityCheckContext = createContext<EligibilityCheckContextType>({
  eligibilityCheckAreas: [],
  isLoadingEligibilityChecks: false,
  createEligibilityCheck: async () => {},
  setSelectedEligibilityAreaIds: () => {},
  isEligibilityVisible: true,
  setEligibilityVisible: () => {},
  isRunningEligibility: false,
  startEligibilityCheck: () => {},
  clearEligibilityCheck: () => {},
})

// Module scope so an empty selection keeps one identity and the derived areas memo holds.
const EMPTY_IDS: ReadonlySet<string> = new Set()

type Selection = { farmId: string | undefined; ids: ReadonlySet<string> }

// No null-guard: ExclusionAreaHome renders consumers outside the provider when
// eligibility is disabled, so they must fall back to the default, not throw.
export const useEligibilityCheckContext = () => useContext(EligibilityCheckContext)

export const EligibilityCheckProvider: FC<PropsWithChildren> = ({ children }) => {
  const { currentFarm } = useContext(AppContext)
  const farmId = currentFarm?.id
  const [isEligibilityVisible, setEligibilityVisible] = useState(true)
  const [selection, setSelection] = useState<Selection>({ farmId, ids: EMPTY_IDS })
  const { isRunningEligibility, startEligibilityCheck, clearEligibilityCheck } =
    useEligibilityStatus(farmId)

  const {
    eligibilityCheckAreas: areas,
    isLoading: isLoadingEligibilityChecks,
    createEligibilityCheck,
  } = useEligibilityCheck({
    farmId,
    query: ELIGIBILITY_QUERY,
    isCheckRunning: isRunningEligibility,
  })

  const selectedIds = selection.farmId === farmId ? selection.ids : EMPTY_IDS

  // Selection is client state: the map and the accordion still read `properties.selected`, so it
  // is projected onto a copy here and the cached server data stays untouched by a refetch.
  const eligibilityCheckAreas = useMemo(
    () =>
      (areas ?? []).map(area => ({
        ...area,
        properties: { ...area.properties, selected: selectedIds.has(area.properties.id) },
      })),
    [areas, selectedIds],
  )

  const setSelectedEligibilityAreaIds = useCallback(
    (ids: Iterable<string>) => setSelection({ farmId, ids: new Set(ids) }),
    [farmId],
  )

  const contextValue = useMemo(
    () => ({
      eligibilityCheckAreas,
      isLoadingEligibilityChecks,
      createEligibilityCheck,
      setSelectedEligibilityAreaIds,
      isEligibilityVisible,
      setEligibilityVisible,
      isRunningEligibility,
      startEligibilityCheck,
      clearEligibilityCheck,
    }),
    [
      eligibilityCheckAreas,
      isLoadingEligibilityChecks,
      createEligibilityCheck,
      setSelectedEligibilityAreaIds,
      isEligibilityVisible,
      setEligibilityVisible,
      isRunningEligibility,
      startEligibilityCheck,
      clearEligibilityCheck,
    ],
  )

  return (
    <EligibilityCheckContext.Provider value={contextValue}>
      {children}
    </EligibilityCheckContext.Provider>
  )
}
